/**
 * Collection grid behaviours for the collection PLP section.
 *   1. Large breakout-card injection from JSON config.
 *   2. Sort dropdown open/close toggle (`.js-collectionSort` / `.js-collectionSortToggle`).
 */

const SORT_SELECTOR = '.js-collectionSort';
const SORT_TOGGLE_SELECTOR = '.js-collectionSortToggle';
const BREAKOUT_JSON_SELECTOR = 'script.breakout-cards-json';
const BREAKOUT_INSERTED = 'breakout-inserted';
// Same markup as assets/icon-chevron-right.svg, which the Liquid breakout card inlines
const CHEVRON_RIGHT_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="6.5" height="6.5" viewBox="0 0 6.5 6.5" overflow="visible" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M1 0L5.5 3.25L1 6.5"/></svg>';

let currentBreakpoint = null;
let resizeBound = false;
let resizeTimer = null;

const resolveFontFamily = (fontName) => {
  switch (fontName) {
    case 'Geogrotesque':
    case 'GeogrotesqueCondBoldWeb':
      return 'GeogrotesqueCondBoldWeb, Arial Black, Impact, sans-serif';
    case 'Base Mono':
    case 'BaseMonoWideWeb':
      return 'BaseMonoWideWeb, Courier, Courier New, sans-serif';
    case 'Copyright':
      return 'RingsideWideWeb, Helvetica, Arial, sans-serif';
    default:
      return fontName ? `'${fontName}', Arial, sans-serif` : 'inherit';
  }
};

/**
 * Escapes a merchant value for an HTML attribute or text node.
 * @param {string} value
 * @returns {string}
 */
const escapeHtml = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
  );

/**
 * Removes markup from a title so it can name a link.
 * @param {string} value
 * @returns {string}
 */
const stripTags = (value) => String(value ?? '').replace(/<[^>]*>/g, '').trim();

const applyBreakoutVars = (card, d) => {
  card.style.setProperty('--bc-bg', d.backgroundColor || '#fff');
  card.style.setProperty('--bc-align', d.contentAlignment || 'left');
  card.style.setProperty('--bc-title-size-d', `${d.titleFontSizeDesktop || 28}px`);
  card.style.setProperty('--bc-title-size-m', `${d.titleFontSizeMobile || 24}px`);
  card.style.setProperty('--bc-content-size-d', `${d.contentFontSizeDesktop || 16}px`);
  card.style.setProperty('--bc-content-size-m', `${d.contentFontSizeMobile || 14}px`);
  card.style.setProperty('--bc-btn-size-d', `${d.buttonFontSizeDesktop || 16}px`);
  card.style.setProperty('--bc-btn-size-m', `${d.buttonFontSizeMobile || 14}px`);
  card.style.setProperty('--bc-btn-color', d.buttonColor || '#fff');
  card.style.setProperty('--bc-btn-bg', d.buttonBackground || '#000');
  card.style.setProperty('--bc-btn-border', d.borderColor || 'transparent');
  card.style.setProperty('--bc-title-bg', d.titleBackgroundColor || 'transparent');

  // Set through the DOM, not the template, so a merchant value cannot break out of the attribute
  const textCta = card.querySelector('.breakout-card__text-cta');
  if (!textCta) return;

  textCta.style.setProperty('--breakout-card-cta-color', d.buttonColor || d.contentColor || 'currentColor');
  textCta.style.setProperty('--breakout-card-cta-font-family', resolveFontFamily(d.contentFontFamily));
};

const buildBreakoutCard = (d) => {
  const multiLineTitle = (() => {
    if (!d.isMultiLineTitle) return [];
    if (Array.isArray(d.multiLineTitle)) return d.multiLineTitle;
    if (typeof d.multiLineTitle === 'string' && d.multiLineTitle) {
      return d.multiLineTitle.includes('||') ? d.multiLineTitle.split('||') : [d.multiLineTitle];
    }
    return [];
  })();

  const titleLines =
    d.isMultiLineTitle && multiLineTitle.length
      ? multiLineTitle.map((line) => `<span>${line}</span>`).join('')
      : d.title
        ? `<span>${d.title}</span>`
        : '';

  // A label with no button background renders as a text CTA with an SVG chevron;
  // the word joiner (&#8288;) keeps the chevron on the label's line
  const buildCta = () => {
    if (!d.buttonLink) return '';

    const href = escapeHtml(d.buttonLink);

    if (!d.buttonLabel) {
      const title = stripTags(multiLineTitle.length ? multiLineTitle.join(' ') : d.title);
      const cardName = escapeHtml(title || stripTags(d.content).split(/\s+/).slice(0, 8).join(' '));
      return `<a href="${href}" data-url="${href}" class="js-productCardLink breakout-card__card-link" aria-label="${cardName}"></a>`;
    }

    if (!d.buttonBackground) {
      const ctaText = escapeHtml(d.buttonLabel.trim().replace(/\s*[>›]$/, ''));
      return `
        <a href="${href}" data-url="${href}" class="js-productCardLink breakout-card__card-link breakout-card__text-cta">
          <span class="breakout-card__text-cta-label">${ctaText}</span>&#8288;<span class="breakout-card__text-cta-chevron" aria-hidden="true">${CHEVRON_RIGHT_SVG}</span>
        </a>`;
    }

    return `
      <a href="${href}" data-url="${href}" class="js-productCardLink breakout-card__card-link">
        <span class="breakout-card__button">${escapeHtml(d.buttonLabel)}</span>
      </a>`;
  };

  const template = `
    <div class="collection__grid-full-width large-breakout-card breakout-card-desktop style-1">
      <div class="collection__grid-full-width-image">
        ${d.cardImageMobile ? `<img class="breakout-card-image-mobile" src="${d.cardImageMobile}" alt="" loading="lazy" width="800" height="600">` : ''}
        ${d.cardImage ? `<img class="breakout-card-image-desktop" src="${d.cardImage}" alt="" loading="lazy" width="1200" height="800">` : ''}
      </div>
      <div class="collection__grid-full-width-text">
        <div class="collection__grid-full-width-text-container">
          <h2 style="
              color: ${d.titleColor || '#000'};
              font-family: ${resolveFontFamily(d.titleFontFamily)};
              ${!d.isMultiLineTitle ? `background-color: ${d.titleBackgroundColor || 'transparent'};` : ''}
            " class="${d.isMultiLineTitle ? 'multi-line-title' : ''}">
            ${titleLines}
          </h2>
          <div class="breakout-card-large-content" style="
              color: ${d.contentColor || '#000'};
              font-family: ${resolveFontFamily(d.contentFontFamily)};
            ">
            ${d.content || ''}
          </div>
          ${buildCta()}
        </div>
      </div>
    </div>
  `;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = template.trim();
  const card = wrapper.firstElementChild;
  applyBreakoutVars(card, d);
  return card;
};

const getSectionRoot = (root) => {
  if (root) return root;
  const info = document.querySelector('collection-info[data-section]');
  return info?.closest('[id^="shopify-section-"]') || document;
};

const getBreakoutCards = (scope) => {
  const el = getSectionRoot(scope).querySelector(BREAKOUT_JSON_SELECTOR);
  if (!el?.textContent?.trim()) return [];
  try {
    const cards = JSON.parse(el.textContent);
    return Array.isArray(cards) ? cards : [];
  } catch {
    return [];
  }
};

const getBreakpoint = () => {
  const width = window.innerWidth;
  if (width >= 1024) return 'desktop';
  if (width >= 768) return 'tablet';
  return 'mobile';
};

const getMultiplier = (breakpoint) =>
  ({ desktop: 4, tablet: 3, mobile: 2 })[breakpoint] || 2;

const removeInsertedBreakouts = (scope) => {
  getSectionRoot(scope).querySelectorAll(`.${BREAKOUT_INSERTED}`).forEach((el) => el.remove());
};

const insertBreakoutCards = (breakpoint, scope) => {
  const root = getSectionRoot(scope);
  const cards = getBreakoutCards(scope);
  if (cards.length === 0) return;

  const productGroups = root.querySelectorAll('.collection__grid .product-group');
  const isGroupedLayout = productGroups.length > 0;
  const multiplier = getMultiplier(breakpoint);
  const productCards = root.querySelectorAll('.collection__grid .product__card');

  cards.forEach((cardData) => {
    const placeIndex = parseInt(cardData.placeIndex, 10);
    if (isNaN(placeIndex) || placeIndex < 1) return;

    const breakoutCard = buildBreakoutCard(cardData);
    breakoutCard.classList.add(BREAKOUT_INSERTED);

    if (isGroupedLayout) {
      const group = productGroups[placeIndex - 1];
      breakoutCard.classList.add('breakout-inserted-in-group');
      if (group) group.appendChild(breakoutCard);
      return;
    }

    const flatIndex = placeIndex * multiplier - 1;
    const targetCard = productCards[flatIndex];
    if (targetCard) targetCard.after(breakoutCard);
  });
};

const refreshBreakoutCards = (scope) => {
  removeInsertedBreakouts(scope);
  if (window.location.search.includes('filter.')) return;
  currentBreakpoint = getBreakpoint();
  insertBreakoutCards(currentBreakpoint, scope);
};

const bindResize = () => {
  if (resizeBound) return;
  resizeBound = true;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const newBreakpoint = getBreakpoint();
      if (newBreakpoint === currentBreakpoint) return;
      refreshBreakoutCards();
    }, 150);
  });
};

const toggleSort = (event) => {
  const elSort = event.currentTarget.closest(SORT_SELECTOR);
  if (elSort) elSort.classList.toggle('open');
};

const changeSort = (event) => {
  const elForm = event.currentTarget;
  const elSort = elForm.closest(SORT_SELECTOR);
  if (elSort) elSort.classList.remove('open');

  const elSelect = elForm
    .closest('.collection__grid-sort-options')
    ?.querySelector('select[name="sort_by"][data-render-section]');

  if (elSelect) {
    elSelect.value = event.target.value;
    requestAnimationFrame(() => {
      elSelect.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
};

const initSort = (root) => {
  const scope = getSectionRoot(root);
  scope.querySelectorAll(SORT_SELECTOR).forEach((el) => {
    el.removeEventListener('change', changeSort);
    el.addEventListener('change', changeSort);
  });
  scope.querySelectorAll(SORT_TOGGLE_SELECTOR).forEach((el) => {
    el.removeEventListener('click', toggleSort);
    el.addEventListener('click', toggleSort);
  });
};

const initAlpineGrid = (root) => {
  if (!root || !window.Alpine?.initTree) return;
  Alpine.initTree(root);
};

const refreshCollectionGrid = (root) => {
  refreshBreakoutCards(root);
  initSort(root);
  initAlpineGrid(root);
};

window.JR = window.JR || {};
window.JR.refreshCollectionGrid = refreshCollectionGrid;

document.addEventListener('theme:page:load', (event) => {
  refreshCollectionGrid(event.detail?.sectionEl || null);
});

const init = () => {
  bindResize();
  refreshCollectionGrid(null);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
